import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import classNames from 'classnames';
import SHLoader from 'common/loader/SHLoader';
import {DESTINATION_DATA} from 'destination/destination-prop-types';
import WithSearch from './WithSearch';
import SearchLayoutDesktop from './layout/SearchLayoutDesktop';
import SpotList from './spot-list/SpotList';
import SpotListSorter from './spot-list/SpotListSorter';
import SpotListSearchControls from './spot-list/SpotListSearchControls';
import SearchMap from './map/SearchMap';
import SpotListSearchControlsCondensed from './spot-list/SpotListSearchControlsCondensed';
import SpotListItemTransient from './spot-list/SpotListItemTransient';

const DestinationInfoPanel = loadable(() =>
    import('../destination/DestinationInfoPanel')
);
const EventList = loadable(() => import('../event/EventList'));
const SpotDetails = loadable(() => import('spot-details/SpotDetails'));

class SearchDesktopDestination extends Component {
    static propTypes = {
        history: PropTypes.shape({
            location: PropTypes.object,
        }),
        sortBy: PropTypes.string,
        starts: PropTypes.string,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        canonicalUrl: PropTypes.string,
        destination: PropTypes.shape(DESTINATION_DATA).isRequired,
        spots: PropTypes.arrayOf(PropTypes.object),
        selectedSpotId: PropTypes.number,
        searchRequestId: PropTypes.string,
        isInfoPanelVisible: PropTypes.bool,
        isEventListEnabled: PropTypes.bool,
        showEventList: PropTypes.bool,
        findParkingNearMe: PropTypes.func,
        onToggleEventList: PropTypes.func.isRequired,
        onSpotDetailsHidden: PropTypes.func,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        onShowDetailsClick: PropTypes.func.isRequired,
        onChangeSort: PropTypes.func.isRequired,
    };
    static defaultProps = {
        isInfoPanelVisible: true,
    };

    _onChangeSort = ({value}) => {
        this.props.onChangeSort(value);
    };

    render() {
        const {
            sortBy,
            starts,
            ends,
            activeSearchTitle,
            isInfoPanelVisible,
            searchRequestId,
            canonicalUrl,
            destination,
            spots,
            selectedSpotId,
            findParkingNearMe,
            onSpotDetailsHidden,
            isEventListEnabled,
            showEventList,
            onToggleEventList,
            isLowInventoryEnabled,
            isSoldOutEnabled,
            onShowDetailsClick,
        } = this.props;
        const classes = classNames('Search', {
            'Search-event-list': showEventList,
        });
        const numSpots = spots.length;
        const showInfoPanel =
            !selectedSpotId && destination?.description && isInfoPanelVisible;
        const sortSelectItems = [
            {value: 'distance', label: 'Sort by Distance'},
            {value: 'price', label: 'Sort by Price'},
        ];
        const transientSpots = spots.filter(({selectedRate}) => {
            // ensure spots with either missing selectedRate
            // or those without the proper selectedRate.rule_type are not offered to users
            return selectedRate?.rule_type !== 'monthly';
        });

        return (
            <div className={classes}>
                {searchRequestId ? (
                    <SearchLayoutDesktop
                        SearchMap={
                            <SearchMap
                                spots={transientSpots}
                                findParkingNearMe={findParkingNearMe}
                                isSoldOutEnabled={isSoldOutEnabled}
                                onShowDetailsClick={onShowDetailsClick}
                            />
                        }
                        SpotDetails={
                            <SpotDetails
                                spotId={selectedSpotId}
                                onCloseClick={onSpotDetailsHidden}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                            />
                        }
                        SpotList={
                            <SpotList
                                spots={transientSpots}
                                selectedSpotId={selectedSpotId}
                                canonicalUrl={canonicalUrl}
                                validFrom={starts}
                                validThrough={ends}
                                isLowInventoryEnabled={isLowInventoryEnabled}
                                isSoldOutEnabled={isSoldOutEnabled}
                                onShowDetailsClick={onShowDetailsClick}
                                SpotListItem={SpotListItemTransient}
                            />
                        }
                        SpotListSorter={
                            <SpotListSorter
                                sortBy={sortBy}
                                sortSelectItems={sortSelectItems}
                                onChangeSort={this._onChangeSort}
                            />
                        }
                        SpotListSearchControls={
                            <SpotListSearchControls
                                numSpots={numSpots}
                                onToggleEventList={onToggleEventList}
                            />
                        }
                        SpotListSearchControlsCondensed={
                            <SpotListSearchControlsCondensed
                                starts={starts}
                                ends={ends}
                                title={activeSearchTitle}
                            />
                        }
                        EventList={
                            <EventList
                                showEventList={showEventList}
                                onToggleEventList={onToggleEventList}
                            />
                        }
                        DestinationInfoPanel={
                            <DestinationInfoPanel
                                title={destination.title}
                                imageId={
                                    destination.default_image?.object.public_id
                                }
                                imageAlt={
                                    destination.default_image?.object.alt_text
                                }
                                address={destination.street_address}
                                city={destination.city}
                                state={destination.state}
                                description={destination.description}
                            />
                        }
                        spots={spots}
                        selectedSpotId={selectedSpotId}
                        isEventListEnabled={isEventListEnabled}
                        showInfoPanel={showInfoPanel}
                        disableKeydownEvents={isSoldOutEnabled}
                    />
                ) : (
                    <SHLoader />
                )}
            </div>
        );
    }
}

const mapStateToProps = ({
    destination: {data: destination},
    search: {
        data: {
            breadcrumbs: {
                activeBreadcrumb: {label: activeSearchTitle},
            },
            isInfoPanelVisible,
        },
    },
    searchRequest: {id: searchRequestId},
}) => {
    return {
        destination,
        activeSearchTitle,
        isInfoPanelVisible,
        searchRequestId,
    };
};

const enhance = compose(WithSearch, connect(mapStateToProps));

export default enhance(SearchDesktopDestination);
